import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { normSC, resolveDirectKey, buildCostCodeMaps } from './budgetVsCommitment'

// Phase PO-4 (architectural consolidation): normSC and Direct Sub-Code ->
// Resource-Code resolution now live exclusively in budgetVsCommitment.js
// (imported above). This file previously had a local resolveKey()
// definition byte-for-byte identical to PurchaseOrders.jsx's
// resolveDirectKey() -- see that module for the single source of truth.

// ─── Amount in Words (SAR) ─────────────────────────────────────────────────
const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen']
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']

function threeDigitsToWords(n) {
  let s = ''
  if (n >= 100) { s += ONES[Math.floor(n / 100)] + ' Hundred '; n %= 100 }
  if (n >= 20) { s += TENS[Math.floor(n / 10)] + ' '; n %= 10 }
  if (n > 0) s += ONES[n] + ' '
  return s.trim()
}

function numberToWords(num) {
  num = Math.floor(Math.abs(num))
  if (num === 0) return 'Zero'
  const groups = ['', 'Thousand', 'Million', 'Billion']
  let parts = []
  let i = 0
  while (num > 0) {
    const chunk = num % 1000
    if (chunk > 0) parts.unshift(threeDigitsToWords(chunk) + (groups[i] ? ' ' + groups[i] : ''))
    num = Math.floor(num / 1000)
    i++
  }
  return parts.join(' ').replace(/\s+/g, ' ').trim()
}

function amountInWords(amount, currency = 'SAR') {
  const whole = Math.floor(amount)
  const fils = Math.round((amount - whole) * 100)
  let s = numberToWords(whole) + ' ' + (currency === 'SAR' ? 'Saudi Riyals' : currency)
  if (fils > 0) s += ' And ' + numberToWords(fils) + ' Halalas'
  return s + ' Only'
}

const fmt = n => n || n === 0 ? Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'

// ─── A row that hides itself if empty (optional-field rule) ────────────────
const Row = ({ label, value }) => {
  if (value === undefined || value === null || value === '' ) return null
  return (
    <div style={{ display: 'flex', fontSize: 11, borderBottom: '1px solid #EDEFF2' }}>
      <div style={{ width: '42%', padding: '5px 8px', color: '#6B7280', fontWeight: 600, background: '#FAFBFC', borderRight: '1px solid #EDEFF2' }}>{label}</div>
      <div style={{ width: '58%', padding: '5px 8px', color: '#0F1017', fontWeight: 600 }}>{value}</div>
    </div>
  )
}

// ═════════════════════════════════════════════════════════════════════════
// PrintDocument — renders PO or PR in a print-ready A4 layout
// Props:
//   docType: 'po' | 'pr'
//   docId:   id of the purchase_orders / purchase_requisitions row
// ═════════════════════════════════════════════════════════════════════════
export default function PrintDocument({ docType, docId, onClose }) {
  const [loading, setLoading] = useState(true)
  const [org, setOrg] = useState(null)
  const [project, setProject] = useState(null)
  const [doc, setDoc] = useState(null)
  const [vendor, setVendor] = useState(null)
  const [linkedPR, setLinkedPR] = useState(null) // PO only: the originating PR
  const [lines, setLines] = useState([])
  const [qtyCheck, setQtyCheck] = useState({}) // PR only: sub_code -> {budget_qty, committed_qty, remaining}
  const [approvals, setApprovals] = useState([]) // PR only
  const [costCode, setCostCode] = useState(null) // PR only
  const [repLines, setRepLines] = useState([]) // rental_equipment only

  useEffect(() => { load() }, [docType, docId])

  async function load() {
    setLoading(true)
    if (docType === 'po') await loadPO()
    else if (docType === 'rental_equipment') await loadRep()
    else if (docType === 'equipment_pr') await loadEquipmentPR()
    else await loadPR()
    setLoading(false)
  }

  async function loadRep() {
    const { data: rep } = await supabase.from('rental_equipment_pos').select('*, cost_codes(code,description)').eq('id', docId).single()
    if (!rep) return
    setDoc(rep)
    const [{ data: projData }, { data: lineData }] = await Promise.all([
      supabase.from('projects').select('*, organizations(*)').eq('id', rep.project_id).single(),
      supabase.from('rental_equipment_po_line_items').select('*, cost_codes(code)').eq('po_id', rep.id).order('created_at'),
    ])
    setProject(projData)
    setOrg(projData?.organizations || null)
    setRepLines(lineData || [])
  }

  async function loadPO() {
    const { data: po } = await supabase.from('purchase_orders').select('*').eq('id', docId).single()
    if (!po) return
    setDoc(po)
    const [{ data: projData }, { data: lineData }, vendorRes, prRes] = await Promise.all([
      supabase.from('projects').select('*, organizations(*)').eq('id', po.project_id).single(),
      supabase.from('po_line_items').select('*').eq('po_id', po.id).order('created_at'),
      po.vendor_id ? supabase.from('vendors').select('*').eq('id', po.vendor_id).single() : Promise.resolve({ data: null }),
      po.pr_id ? supabase.from('purchase_requisitions').select('pr_number').eq('id', po.pr_id).single() : Promise.resolve({ data: null }),
    ])
    setProject(projData)
    setOrg(projData?.organizations || null)
    setLines(lineData || [])
    setVendor(vendorRes.data || null)
    setLinkedPR(prRes.data || null)
  }

  async function loadPR() {
    const { data: pr } = await supabase.from('purchase_requisitions').select('*').eq('id', docId).single()
    if (!pr) return
    setDoc(pr)
    const [{ data: projData }, { data: lineData }, { data: ccData }] = await Promise.all([
      supabase.from('projects').select('*, organizations(*)').eq('id', pr.project_id).single(),
      supabase.from('pr_line_items').select('*').eq('pr_id', pr.id).order('created_at'),
      pr.cost_code_id ? supabase.from('cost_codes').select('code,description').eq('id', pr.cost_code_id).single() : Promise.resolve({ data: null }),
    ])
    setProject(projData)
    setOrg(projData?.organizations || null)
    setLines(lineData || [])
    setCostCode(ccData || null)

    // Phase PO-4: Budget vs requested qty comparison, now grouped by
    // Resource Code (Level 4) instead of raw Sub Code -- mirrors the
    // PurchaseOrders.jsx Phase PO-3 redesign. PRs are always Direct
    // (purchase_requisitions has no cost_type/indirect concept), so no
    // Direct/Indirect branching is needed here.
    const subCodes = [...new Set((lineData || []).map(l => l.sub_code).filter(Boolean))]
    if (subCodes.length && pr.project_id) {
      const [{ data: costCodesTree }, { data: tenderRows }, { data: poLines }] = await Promise.all([
        supabase.from('cost_codes').select('id, code, level, parent_id').eq('project_id', pr.project_id),
        supabase
          .from('tender_direct_items')
          .select('resource_code, row_type, qty')
          .eq('project_id', pr.project_id)
          .in('row_type', ['material', 'labour', 'subcontract', 'equipment']),
        supabase
          .from('po_line_items')
          .select('sub_code, qty, purchase_orders!inner(project_id, status, cost_type)')
          .eq('purchase_orders.project_id', pr.project_id)
          .in('purchase_orders.status', ['approved', 'closed']),
      ])
      const maps = buildCostCodeMaps(costCodesTree)
      const resolveKey = (subCode) => resolveDirectKey(subCode, maps)

      const budgetQtyMap = {}
      ;(tenderRows || []).forEach(r => {
        const k = normSC(r.resource_code)
        if (!k) return
        budgetQtyMap[k] = (budgetQtyMap[k] || 0) + (parseFloat(r.qty) || 0)
      })
      const committedQtyMap = {}
      ;(poLines || []).filter(l => (l.purchase_orders?.cost_type||'direct') !== 'indirect').forEach(l => {
        const r = resolveKey(l.sub_code)
        if (!r.key) return
        committedQtyMap[r.key] = (committedQtyMap[r.key] || 0) + (parseFloat(l.qty) || 0)
      })

      const check = {}
      subCodes.forEach(sc => {
        const r = resolveKey(sc)
        if (r.kind === 'unknown') { check[sc] = { kind: 'unknown', label: r.label }; return }
        if (r.kind === 'legacy') { check[sc] = { kind: 'legacy', label: r.label }; return }
        const budgetQty = budgetQtyMap[r.key] || 0
        const committedQty = committedQtyMap[r.key] || 0
        check[sc] = { kind: 'resource', label: r.label, budget_qty: budgetQty, committed_qty: committedQty, remaining: budgetQty - committedQty }
      })
      setQtyCheck(check)
    }

    setApprovals([
      { stage: 'Site Engineer',    by: pr.requested_by_name,  at: pr.created_at,       done: true },
      { stage: 'Cost Control',     by: pr.cc_approved_by,     at: pr.cc_approved_at,   done: !!pr.cc_approved_by },
      { stage: 'Procurement',      by: pr.proc_approved_by,   at: pr.proc_approved_at, done: !!pr.proc_approved_by },
    ])
  }

  async function loadEquipmentPR() {
    const { data: pr } = await supabase.from('equipment_prs').select('*').eq('id', docId).single()
    if (!pr) return
    setDoc(pr)
    const [{ data: projData }, { data: lineData }, { data: ccData }] = await Promise.all([
      supabase.from('projects').select('*, organizations(*)').eq('id', pr.project_id).single(),
      supabase.from('equipment_pr_line_items').select('*').eq('pr_id', pr.id).order('created_at'),
      pr.cost_code_id ? supabase.from('cost_codes').select('code,description').eq('id', pr.cost_code_id).single() : Promise.resolve({ data: null }),
    ])
    setProject(projData)
    setOrg(projData?.organizations || null)
    // Normalize equipment lines to the same shape loadPR's lines use (description, unit, est_qty, sub_code)
    setLines((lineData || []).map(l => ({
      ...l,
      description: l.description || l.equipment_type,
      sub_code: l.sub_code || l.equipment_type,
      est_qty: l.est_qty,
    })))
    setCostCode(ccData || null)

    // Budget vs requested qty comparison (equipment version) — now via the
    // shared Resource Code SSOT (resolveDirectKey/buildCostCodeMaps from
    // budgetVsCommitment.js), replacing the legacy budget_equipment_code
    // column (confirmed 0% populated — 0/62 rows in tender_direct_items,
    // row_type='equipment' — while resource_code is 100% populated). Mirrors
    // PurchaseOrders.jsx's Phase PO-3 Direct resolution, not a separate
    // Equipment-specific mapping.
    const subCodes = [...new Set((lineData || []).map(l => l.sub_code || l.equipment_type).filter(Boolean))]
    if (subCodes.length && pr.project_id) {
      const [{ data: costCodesTree }, { data: tenderRows }, { data: eqPoLines }] = await Promise.all([
        supabase.from('cost_codes').select('id, code, level, parent_id').eq('project_id', pr.project_id),
        supabase
          .from('tender_direct_items')
          .select('resource_code, row_type, qty')
          .eq('project_id', pr.project_id)
          .eq('row_type', 'equipment'),
        supabase
          .from('rental_equipment_po_line_items')
          .select('equipment_type, quantity, rental_equipment_pos!inner(project_id, status)')
          .eq('rental_equipment_pos.project_id', pr.project_id),
      ])
      const maps = buildCostCodeMaps(costCodesTree)
      const budgetQtyMap = {}
      ;(tenderRows || []).forEach(r => {
        const k = normSC(r.resource_code)
        if (!k) return
        budgetQtyMap[k] = (budgetQtyMap[k] || 0) + (parseFloat(r.qty) || 0)
      })
      const check = {}
      subCodes.forEach(sc => {
        const r = resolveDirectKey(sc, maps)
        if (r.kind === 'unknown') { check[sc] = { kind: 'unknown', label: r.label }; return }
        if (r.kind === 'legacy') { check[sc] = { kind: 'legacy', label: r.label }; return }
        const budgetQty = budgetQtyMap[r.key] || 0
        const committedQty = (eqPoLines || [])
          .filter(l => resolveDirectKey(l.equipment_type, maps).key === r.key && ['approved', 'closed'].includes(l.rental_equipment_pos?.status))
          .reduce((s, l) => s + (parseFloat(l.quantity) || 0), 0)
        check[sc] = { kind: 'resource', label: r.label, budget_qty: budgetQty, committed_qty: committedQty, remaining: budgetQty - committedQty }
      })
      setQtyCheck(check)
    }

    setApprovals([
      { stage: 'Site Engineer',    by: pr.requested_by_name,  at: pr.created_at,       done: true },
      { stage: 'Cost Control',     by: pr.cc_approved_by,     at: pr.cc_approved_at,   done: !!pr.cc_approved_by },
      { stage: 'Procurement',      by: pr.proc_approved_by,   at: pr.proc_approved_at, done: !!pr.proc_approved_by },
    ])
  }

  function handlePrint() { window.print() }

  if (loading) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(15,16,23,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#fff', borderRadius: 10, padding: '24px 32px', fontSize: 13, color: '#6B7280' }}>Loading document…</div>
      </div>
    )
  }
  if (!doc) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(15,16,23,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#fff', borderRadius: 10, padding: '24px 32px', fontSize: 13, color: '#DC2626' }}>
          Document not found.
          <button onClick={onClose} style={{ display: 'block', marginTop: 12, fontSize: 12, color: '#6B7280', cursor: 'pointer', background: 'none', border: 'none' }}>Close</button>
        </div>
      </div>
    )
  }

  const isPO = docType === 'po'
  const isRep = docType === 'rental_equipment'
  const totalExc = (isPO || isRep) ? doc.total_exc_vat : 0
  const vatPct = (isPO || isRep) ? (doc.vat_pct || 15) : 0
  const vatAmt = (isPO || isRep) ? (doc.total_inc_vat - doc.total_exc_vat) : 0
  const totalInc = (isPO || isRep) ? doc.total_inc_vat : 0

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: '#43464D', overflowY: 'auto' }}>
      {/* Toolbar — hidden on print */}
      <div className="print-hide" style={{ position: 'sticky', top: 0, zIndex: 10, background: '#1C1E24', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>
          {isPO ? 'Purchase Order' : isRep ? 'Rental Equipment PO' : docType === 'equipment_pr' ? 'Equipment Purchase Requisition' : 'Purchase Requisition'} — {isPO ? doc.po_number : isRep ? doc.po_number : (doc.pr_number || doc.id?.slice(0, 8))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handlePrint} style={{ padding: '7px 16px', borderRadius: 6, background: '#1A7A4A', color: '#fff', border: 'none', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>🖨 Print / Save PDF</button>
          <button onClick={onClose} style={{ padding: '7px 16px', borderRadius: 6, background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,.25)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>✕ Close</button>
        </div>
      </div>

      {/* Print page */}
      <div className="print-page" style={{ width: 794, minHeight: 1123, margin: '24px auto', background: '#fff', boxShadow: '0 4px 24px rgba(0,0,0,.3)', padding: '32px 36px', boxSizing: 'border-box', fontFamily: "'Segoe UI', Arial, sans-serif", color: '#0F1017' }}>

        {/* ── Header ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '3px solid #1B3A6B', paddingBottom: 14, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {org?.logo_url ? (
              <img src={org.logo_url} alt="logo" style={{ width: 60, height: 60, objectFit: 'contain' }} />
            ) : (
              <div style={{ width: 60, height: 60, borderRadius: 8, background: '#1B3A6B', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 22, fontWeight: 800 }}>
                {org?.name?.charAt(0) || 'C'}
              </div>
            )}
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#1B3A6B' }}>{org?.name || 'Company Name'}</div>
              {org?.name_ar && <div style={{ fontSize: 12, color: '#6B7280', direction: 'rtl' }}>{org.name_ar}</div>}
              <div style={{ fontSize: 9.5, color: '#9CA3AF', marginTop: 2, display: 'flex', gap: 10 }}>
                {org?.cr_number && <span>CR: {org.cr_number}</span>}
                {org?.vat_number && <span>VAT: {org.vat_number}</span>}
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#1B3A6B', letterSpacing: '.02em' }}>
              {isPO ? 'PURCHASE ORDER' : isRep ? 'RENTAL EQUIPMENT PURCHASE ORDER' : docType === 'equipment_pr' ? 'EQUIPMENT PURCHASE REQUISITION' : 'PURCHASE REQUISITION'}
            </div>
            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
              No. <span style={{ fontWeight: 700, color: '#0F1017', fontFamily: 'monospace' }}>{isPO ? doc.po_number : isRep ? doc.po_number : (doc.pr_number || doc.id?.slice(0, 8))}</span>
            </div>
            <div style={{ fontSize: 11, color: '#6B7280' }}>
              Date: <span style={{ fontWeight: 700, color: '#0F1017' }}>{isPO ? doc.issue_date : isRep ? doc.issue_date : doc.created_at?.split('T')[0]}</span>
            </div>
            {isRep && (
              <div style={{ marginTop: 4, display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: doc.approval_stage === 'approved' ? '#DCFCE7' : '#FEF3C7', color: doc.approval_stage === 'approved' ? '#14532D' : '#92400E' }}>
                {(doc.approval_stage || doc.status || '').toUpperCase()}
              </div>
            )}
            {!isPO && !isRep && (
              <div style={{ marginTop: 4, display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: doc.status === 'approved' ? '#DCFCE7' : '#FEF3C7', color: doc.status === 'approved' ? '#14532D' : '#92400E' }}>
                {(doc.approval_stage || doc.status || '').toUpperCase()}
              </div>
            )}
          </div>
        </div>

        {/* ── Two-column info: Company/Project vs Vendor(PO)/Request(PR) ── */}
        <div style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
          <div style={{ flex: 1, border: '1px solid #E5E7EB', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ background: '#1B3A6B', color: '#fff', fontSize: 10, fontWeight: 700, padding: '5px 8px', textTransform: 'uppercase', letterSpacing: '.05em' }}>Project Information</div>
            <Row label="Project Name" value={project?.name} />
            <Row label="Project No." value={project?.project_no} />
            <Row label="Project Address" value={project?.project_address} />
            {isPO && <Row label="PR Reference" value={linkedPR?.pr_number} />}
            {isPO && <Row label="Requested By" value={doc.requested_by} />}
            {isRep && <Row label="Cost Code" value={doc.cost_codes ? `${doc.cost_codes.code} — ${doc.cost_codes.description}` : null} />}
            {!isPO && !isRep && <Row label="Requested By" value={doc.requested_by_name} />}
            {!isPO && !isRep && <Row label="Department" value={doc.department} />}
            {!isPO && !isRep && <Row label="Priority" value={doc.priority} />}
            {!isPO && !isRep && <Row label="Required By" value={doc.required_by} />}
          </div>

          <div style={{ flex: 1, border: '1px solid #E5E7EB', borderRadius: 6, overflow: 'hidden' }}>
            {isPO ? (
              <>
                <div style={{ background: '#1B3A6B', color: '#fff', fontSize: 10, fontWeight: 700, padding: '5px 8px', textTransform: 'uppercase', letterSpacing: '.05em' }}>Vendor Information</div>
                <Row label="Vendor Name" value={vendor?.trade_name || doc.vendor} />
                <Row label="Legal Name" value={vendor?.legal_name} />
                <Row label="CR Number" value={vendor?.cr_number} />
                <Row label="VAT Number" value={vendor?.vat_number} />
                <Row label="Contact Person" value={vendor?.contact_name} />
                <Row label="Phone" value={vendor?.contact_phone} />
                <Row label="Email" value={vendor?.contact_email} />
              </>
            ) : isRep ? (
              <>
                <div style={{ background: '#1B3A6B', color: '#fff', fontSize: 10, fontWeight: 700, padding: '5px 8px', textTransform: 'uppercase', letterSpacing: '.05em' }}>Vendor Information</div>
                <Row label="Vendor Name" value={doc.vendor} />
                <Row label="Contract Ref" value={doc.contract_ref} />
                <Row label="Notes" value={doc.notes} />
              </>
            ) : (
              <>
                <div style={{ background: '#1B3A6B', color: '#fff', fontSize: 10, fontWeight: 700, padding: '5px 8px', textTransform: 'uppercase', letterSpacing: '.05em' }}>Requisition Details</div>
                <Row label="Cost Code" value={costCode ? `${costCode.code} — ${costCode.description}` : null} />
                <Row label="Notes" value={doc.notes} />
              </>
            )}
          </div>
        </div>

        {/* ── Bank Info (PO only, optional section) ── */}
        {isPO && vendor && (vendor.bank_name || vendor.bank_iban || vendor.bank_swift) && (
          <div style={{ border: '1px solid #E5E7EB', borderRadius: 6, overflow: 'hidden', marginBottom: 14 }}>
            <div style={{ background: '#F3F4F6', fontSize: 10, fontWeight: 700, padding: '5px 8px', textTransform: 'uppercase', letterSpacing: '.05em', color: '#374151' }}>Bank Information</div>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              <Row label="Bank Name" value={vendor.bank_name} />
              <Row label="Beneficiary Name" value={vendor.beneficiary_name || vendor.legal_name} />
              <Row label="IBAN No." value={vendor.bank_iban} />
              <Row label="Swift Code" value={vendor.bank_swift} />
            </div>
          </div>
        )}

        {/* ── Line Items ── */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10.5, marginBottom: 14 }}>
          <thead>
            <tr style={{ background: '#1B3A6B', color: '#fff' }}>
              <th style={{ padding: '6px 6px', textAlign: 'center', width: 26 }}>#</th>
              <th style={{ padding: '6px 6px', textAlign: 'left' }}>{isRep ? 'Equipment Type' : 'Description'}</th>
              {isPO && <th style={{ padding: '6px 6px', textAlign: 'left', width: 65 }}>Ref Code</th>}
              {isPO && <th style={{ padding: '6px 6px', textAlign: 'left', width: 65 }}>Sub Code</th>}
              {isRep && <th style={{ padding: '6px 6px', textAlign: 'left', width: 65 }}>Cost Code</th>}
              <th style={{ padding: '6px 6px', textAlign: 'center', width: 55 }}>Unit</th>
              <th style={{ padding: '6px 6px', textAlign: 'right', width: 55 }}>{(isPO || isRep) ? 'Qty' : 'Est. Qty'}</th>
              {(isPO || isRep) && <th style={{ padding: '6px 6px', textAlign: 'right', width: 75 }}>Unit Price</th>}
              {(isPO || isRep) && <th style={{ padding: '6px 6px', textAlign: 'right', width: 85 }}>Total</th>}
              {!isPO && !isRep && <th style={{ padding: '6px 6px', textAlign: 'center', width: 100 }}>Budget Status</th>}
            </tr>
          </thead>
          <tbody>
            {isRep ? (
              repLines.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 14, textAlign: 'center', color: '#9CA3AF' }}>No line items</td></tr>
              ) : repLines.map((l, i) => (
                <tr key={l.id || i} style={{ background: i % 2 === 0 ? '#fff' : '#FAFBFC', borderBottom: '1px solid #EDEFF2' }}>
                  <td style={{ padding: '5px 6px', textAlign: 'center', color: '#9CA3AF' }}>{i + 1}</td>
                  <td style={{ padding: '5px 6px' }}>{l.equipment_type}</td>
                  <td style={{ padding: '5px 6px', fontFamily: 'monospace', fontSize: 9.5 }}>{l.cost_codes?.code || '—'}</td>
                  <td style={{ padding: '5px 6px', textAlign: 'center' }}>{l.unit || '—'}</td>
                  <td style={{ padding: '5px 6px', textAlign: 'right' }}>{l.quantity || 0}</td>
                  <td style={{ padding: '5px 6px', textAlign: 'right' }}>{fmt(l.rate)}</td>
                  <td style={{ padding: '5px 6px', textAlign: 'right', fontWeight: 700 }}>{fmt(l.total_cost)}</td>
                </tr>
              ))
            ) : lines.length === 0 ? (
              <tr><td colSpan={isPO ? 7 : 5} style={{ padding: 14, textAlign: 'center', color: '#9CA3AF' }}>No line items</td></tr>
            ) : lines.map((l, i) => {
              const qc = !isPO && l.sub_code ? qtyCheck[l.sub_code] : null
              // Phase PO-4: qc.kind distinguishes a real resource-code
              // match ('resource', has .remaining) from 'legacy' (no
              // Resource Code mapping exists) and 'unknown' (sub_code not
              // found in cost_codes at all) -- neither of the latter two
              // should silently read as "Within Budget".
              const overBudget = qc && qc.kind === 'resource' && (parseFloat(l.est_qty) || 0) > qc.remaining
              return (
                <tr key={l.id || i} style={{ background: i % 2 === 0 ? '#fff' : '#FAFBFC', borderBottom: '1px solid #EDEFF2' }}>
                  <td style={{ padding: '5px 6px', textAlign: 'center', color: '#9CA3AF' }}>{i + 1}</td>
                  <td style={{ padding: '5px 6px' }}>{l.description}</td>
                  {isPO && <td style={{ padding: '5px 6px', fontFamily: 'monospace', fontSize: 9.5 }}>{l.ref_code || '—'}</td>}
                  {isPO && <td style={{ padding: '5px 6px', fontFamily: 'monospace', fontSize: 9.5 }}>{l.sub_code || '—'}</td>}
                  <td style={{ padding: '5px 6px', textAlign: 'center' }}>{l.unit || '—'}</td>
                  <td style={{ padding: '5px 6px', textAlign: 'right' }}>{isPO ? (l.qty || 0) : (l.est_qty || '—')}</td>
                  {isPO && <td style={{ padding: '5px 6px', textAlign: 'right' }}>{fmt(l.unit_rate)}</td>}
                  {isPO && <td style={{ padding: '5px 6px', textAlign: 'right', fontWeight: 700 }}>{fmt(l.amount_exc_vat || ((l.qty || 0) * (l.unit_rate || 0)))}</td>}
                  {!isPO && (
                    <td style={{ padding: '5px 6px', textAlign: 'center' }}>
                      {!qc || qc.kind === 'unknown' ? (
                        <span style={{ color: '#9CA3AF' }}>—</span>
                      ) : qc.kind === 'legacy' ? (
                        <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 9, fontWeight: 700, background: '#F3F4F6', color: '#6B7280' }}>Legacy — No Mapping</span>
                      ) : overBudget ? (
                        <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 9, fontWeight: 700, background: '#FEE2E2', color: '#B91C1C' }}>Over Budget</span>
                      ) : (
                        <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 9, fontWeight: 700, background: '#DCFCE7', color: '#14532D' }}>Within Budget</span>
                      )}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>

        {/* ── Totals (PO / Rental Equipment) ── */}
        {(isPO || isRep) && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
            <div style={{ width: 260 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', fontSize: 11 }}>
                <span style={{ color: '#6B7280' }}>Sub Total (Exc. VAT)</span>
                <span style={{ fontWeight: 700 }}>{fmt(totalExc)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', fontSize: 11 }}>
                <span style={{ color: '#6B7280' }}>VAT ({vatPct}%)</span>
                <span style={{ fontWeight: 700 }}>{fmt(vatAmt)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 8px', fontSize: 13, background: '#1B3A6B', color: '#fff', borderRadius: 4, marginTop: 4 }}>
                <span style={{ fontWeight: 700 }}>Total (Inc. VAT)</span>
                <span style={{ fontWeight: 800 }}>{fmt(totalInc)} {project?.currency || 'SAR'}</span>
              </div>
            </div>
          </div>
        )}

        {/* ── Amount in words (PO / Rental Equipment) ── */}
        {(isPO || isRep) && (
          <div style={{ border: '1px solid #E5E7EB', borderRadius: 6, padding: '7px 10px', fontSize: 10.5, marginBottom: 14, background: '#FAFBFC' }}>
            <span style={{ fontWeight: 700, color: '#6B7280' }}>Amount in Words: </span>
            {amountInWords(totalInc, project?.currency || 'SAR')}
          </div>
        )}

        {/* ── Payment / Delivery terms (PO only, optional) ── */}
        {isPO && (doc.payment_terms || vendor?.payment_terms || doc.delivery_term) && (
          <div style={{ display: 'flex', gap: 14, marginBottom: 14, fontSize: 10.5 }}>
            {(doc.delivery_term) && <div><span style={{ color: '#6B7280', fontWeight: 600 }}>Delivery Term: </span>{doc.delivery_term}</div>}
            {(doc.payment_terms || vendor?.payment_terms) && <div><span style={{ color: '#6B7280', fontWeight: 600 }}>Payment Term: </span>{doc.payment_terms || vendor?.payment_terms}</div>}
          </div>
        )}

        {/* ── Approval trail (PR only) ── */}
        {!isPO && !isRep && (
          <div style={{ border: '1px solid #E5E7EB', borderRadius: 6, overflow: 'hidden', marginBottom: 16 }}>
            <div style={{ background: '#F3F4F6', fontSize: 10, fontWeight: 700, padding: '5px 8px', textTransform: 'uppercase', letterSpacing: '.05em', color: '#374151' }}>Approval Trail</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10.5 }}>
              <thead>
                <tr style={{ background: '#FAFBFC' }}>
                  <th style={{ padding: '5px 8px', textAlign: 'left', color: '#6B7280', fontWeight: 700 }}>Stage</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left', color: '#6B7280', fontWeight: 700 }}>By</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left', color: '#6B7280', fontWeight: 700 }}>Date</th>
                  <th style={{ padding: '5px 8px', textAlign: 'center', color: '#6B7280', fontWeight: 700 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {approvals.map((a, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #EDEFF2' }}>
                    <td style={{ padding: '5px 8px' }}>{a.stage}</td>
                    <td style={{ padding: '5px 8px' }}>{a.by || '—'}</td>
                    <td style={{ padding: '5px 8px' }}>{a.at ? String(a.at).split('T')[0] : '—'}</td>
                    <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                      {a.done
                        ? <span style={{ color: '#14532D', fontWeight: 700 }}>✓ Done</span>
                        : <span style={{ color: '#9CA3AF' }}>Pending</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Signatures ── */}
        <div style={{ display: 'flex', gap: 20, marginTop: 30, paddingTop: 14, borderTop: '1px dashed #D1D5DB' }}>
          {(isPO ? ['Prepared By', 'Reviewed By', 'Approved By'] : isRep ? ['Cost Control', 'Director', 'Approved By'] : ['Requested By', 'Cost Control', 'Procurement']).map(label => (
            <div key={label} style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ height: 40, borderBottom: '1px solid #9CA3AF' }} />
              <div style={{ fontSize: 9.5, color: '#6B7280', marginTop: 4, textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
            </div>
          ))}
        </div>

        {/* ── Footer ── */}
        <div style={{ marginTop: 20, paddingTop: 10, borderTop: '1px solid #E5E7EB', fontSize: 8.5, color: '#9CA3AF', textAlign: 'center' }}>
          {org?.name || 'CostSpace'} {org?.address ? '· ' + org.address : ''} {org?.phone ? '· ' + org.phone : ''}
          {' '}— Generated by CostSpace Pro
        </div>
      </div>

      <style>{`
        @media print {
          .print-hide { display: none !important; }
          body { margin: 0; }
          .print-page { box-shadow: none !important; margin: 0 !important; width: 100% !important; }
        }
        @page { size: A4; margin: 0; }
      `}</style>
    </div>
  )
}
